import { useState } from "react";
import { updateSettings } from "../APIs/api-routes.util";
import { AppSettingsResponse } from "../APIs/api.types";
import { Alert } from "../components/ui/Alert";
import { LoadingOverlay } from "../components/ui/PagePrimitives";
import { useAppSettings } from "../context/AppSettingsContext";
import { apiSubscribe } from "../lib/api-subscribe";
import { parseUrgentEnabled } from "../lib/app-settings.util";
import { cn } from "../lib/cn";

function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        "relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-40",
        checked ? "bg-brand-600" : "bg-slate-300"
      )}
    >
      <span
        className={cn(
          "inline-block h-6 w-6 rounded-full bg-white shadow-sm transition-transform duration-200",
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        )}
      />
    </button>
  );
}

function Settings() {
  const { urgentEnabled, setUrgentEnabled } = useAppSettings();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const handleToggle = () => {
    const next = !urgentEnabled;
    setLoading(true);
    setError("");
    setSaved(false);
    setUrgentEnabled(next);
    apiSubscribe<AppSettingsResponse>(updateSettings({ enable_urgent_bookings: next }), {
      next: (body) => {
        setLoading(false);
        setUrgentEnabled(parseUrgentEnabled(body.enable_urgent_bookings));
        setSaved(true);
      },
      error: () => {
        setLoading(false);
        setUrgentEnabled(!next);
        setError("Could not save settings.");
      },
    });
  };

  return (
    <>
      <LoadingOverlay show={loading} />

      {error && (
        <Alert variant="error" onDismiss={() => setError("")}>
          {error}
        </Alert>
      )}

      {saved && (
        <Alert variant="success" onDismiss={() => setSaved(false)}>
          Settings saved.
        </Alert>
      )}

      <div className="rounded-xl bg-brand-50 px-4 py-3 text-sm text-content-secondary">
        Control whether patients and staff can book urgent appointments.
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-soft">
        <div className="flex items-center gap-4 px-4 py-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-content-primary">Enable urgent bookings</p>
            <p className="mt-1 text-xs leading-relaxed text-content-muted">
              When off, WhatsApp starts with normal booking only and the admin Book screen hides
              the urgent option. Existing urgent appointments stay visible.
            </p>
          </div>
          <Toggle
            checked={urgentEnabled}
            disabled={loading}
            onChange={handleToggle}
            label="Enable urgent bookings"
          />
        </div>
      </div>
    </>
  );
}

export default Settings;
